/* eslint-disable react/no-multi-comp */
/* eslint-disable react/display-name */
import React from 'react';
import BarChartIcon from '@material-ui/icons/BarChart';
import DashboardIcon from '@material-ui/icons/DashboardOutlined';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import AssignmentInd from '@material-ui/icons/AssignmentInd';
import CardMembershipIcon from '@material-ui/icons/CardMembership';
import StyleIcon from '@material-ui/icons/Style';
import {
  Assignment,
  Email,
  Flip,
  HowToReg,
  PageviewOutlined,
  PolicyOutlined,
  History
} from '@material-ui/icons';
import { useQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import { Tooltip } from '@material-ui/core';
import { Chip } from '@mui/material';

const BackgroundCheckNotificationsQuery = gql`
  query Notifications {
    backgroundChecks(filter: { checkStatus: "ready" }) {
      pageInfo {
        total
      }
    }
  }
`;

const ObservationNotificationsQuery = gql`
  query ObservationNotifications {
    observationNotifications {
      tutorsWithoutNextObservation
      observationsPastDue
    }
  }
`;

// certificateQARemaining: Int!
const CertificateQAQuery = gql`
  query CertificateQA {
    certificateQARemaining
  }
`;

const ClassroomQAQuery = gql`
  query ClassroomQA {
    classroomQARemaining
  }
`;

const SyllabusIcon = ({ className, style }) => {
  return (
    <svg
      width="16"
      className={className}
      height="20"
      viewBox="0 0 16 20"
      style={{ marginLeft: '5px', ...style }}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4 10V8H12V10H4ZM4 6V4H12V6H4ZM2 12H9.5C9.98333 12 10.4333 12.1042 10.85 12.3125C11.2667 12.5208 11.6167 12.8167 11.9 13.2L14 15.95V2H2V12ZM2 18H13.05L10.325 14.425C10.225 14.2917 10.1042 14.1875 9.9625 14.1125C9.82083 14.0375 9.66667 14 9.5 14H2V18ZM14 20H2C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H14C14.55 0 15.0208 0.195833 15.4125 0.5875C15.8042 0.979167 16 1.45 16 2V18C16 18.55 15.8042 19.0208 15.4125 19.4125C15.0208 19.8042 14.55 20 14 20Z"
        fill="#5F6368"
      />
    </svg>
  );
};

const CheckNotification = () => {
  const { loading, error, data: queryData } = useQuery(
    BackgroundCheckNotificationsQuery,
    {
      fetchPolicy: 'cache-and-network'
    }
  );

  if (error) return null;
  if (loading) return null;
  if (queryData?.backgroundChecks?.pageInfo?.total === 0) {
    return null;
  }

  return (
    <>
      {queryData?.backgroundChecks?.pageInfo?.total > 0 && (
        <Tooltip
          title={`${queryData?.backgroundChecks?.pageInfo?.total} checks completed by delegates`}
        >
          <Chip
            label={queryData?.backgroundChecks?.pageInfo?.total ?? '?'}
            color="primary"
            size="small"
          />
        </Tooltip>
      )}
    </>
  );
};

const LessonObservationNotification = () => {
  const { loading, error, data: queryData } = useQuery(
    ObservationNotificationsQuery,
    {
      fetchPolicy: 'cache-and-network'
    }
  );

  if (error) return null;
  if (loading) return null;

  return (
    <>
      {queryData?.observationNotifications.tutorsWithoutNextObservation > 0 && (
        <Tooltip
          title={`${queryData?.observationNotifications.tutorsWithoutNextObservation} tutors without scheduled observation`}
        >
          <Chip
            label={
              queryData?.observationNotifications.tutorsWithoutNextObservation
            }
            color="info"
            size="small"
          />
        </Tooltip>
      )}
      {queryData?.observationNotifications.observationsPastDue > 0 && (
        <Tooltip
          title={`${queryData?.observationNotifications.observationsPastDue} observations past due`}
        >
          <Chip
            label={queryData?.observationNotifications.observationsPastDue}
            color="warning"
            size="small"
          />
        </Tooltip>
      )}
    </>
  );
};

const CertificateQANotification = () => {
  const { loading, error, data: queryData } = useQuery(CertificateQAQuery, {
    fetchPolicy: 'cache-and-network'
  });

  if (error) return null;
  if (loading) return null;

  return (
    <>
      {queryData?.certificateQARemaining > 0 && (
        <Tooltip
          title={`${queryData?.certificateQARemaining} certificates requiring review`}
        >
          <Chip
            label={queryData?.certificateQARemaining}
            color="primary"
            size="small"
          />
        </Tooltip>
      )}
    </>
  );
};

const ClassroomQANotification = () => {
  const { loading, error, data: queryData } = useQuery(ClassroomQAQuery, {
    fetchPolicy: 'cache-and-network'
  });

  if (error) return null;
  if (loading) return null;

  return (
    <>
      {queryData?.classroomQARemaining > 0 && (
        <Tooltip
          title={`${queryData?.classroomQARemaining} classroom QA items requiring review`}
        >
          <Chip
            label={queryData?.classroomQARemaining}
            color="primary"
            size="small"
          />
        </Tooltip>
      )}
    </>
  );
};

export const navConfig = [
  {
    subheader: 'Management',
    items: [
      {
        title: 'Dashboard',
        href: '/dashboard',
        icon: DashboardIcon
      },
      {
        title: 'Companies',
        href: '/companies',
        icon: BarChartIcon
      },
      {
        title: 'Individuals',
        href: '/individuals',
        icon: AssignmentInd
      },
      {
        title: 'Delegates',
        href: '/delegates',
        icon: AssignmentInd
      },
      {
        title: 'Managers',
        href: '/managers',
        icon: AssignmentInd
      },
      {
        title: 'Company Requests',
        href: '/approve-companies',
        icon: BarChartIcon
      },
      {
        title: 'Xero Contacts',
        href: '/xero-contacts',
        icon: BarChartIcon
      },
      {
        title: 'Certificate Expiries',
        href: '/expiries',
        icon: BarChartIcon
      },
      {
        title: 'Background Checks',
        href: '/background-checks',
        icon: HowToReg,
        notifications: CheckNotification
      },
      {
        title: 'XRAY Simulator',
        href: '/simulator',
        icon: Flip
      }
    ]
  },
  {
    subheader: 'Courses',
    items: [
      {
        title: 'Courses',
        href: '/courses',
        icon: LibraryBooks,
        items: [
          {
            title: 'Courses',
            href: '/courses'
          },
          {
            title: 'Modules',
            href: '/modules'
          },
          {
            title: 'Tests',
            href: '/tests'
          }
        ]
      },
      {
        title: 'Certificate Types',
        href: '/certificateTypes',
        icon: CardMembershipIcon
      },
      {
        title: 'CAA Numbers',
        href: '/caaNumbers',
        icon: CardMembershipIcon
      },
      {
        title: 'Categories',
        href: '/categories',
        icon: CardMembershipIcon
      },
      {
        title: 'Secondary Categories',
        href: '/secondarycategories',
        icon: CardMembershipIcon
      },
      {
        title: 'Tutors',
        href: '/tutors',
        icon: AssignmentInd
      },
      {
        title: 'Tags',
        href: '/tags',
        icon: StyleIcon
      },
      {
        title: 'Audit Templates',
        href: '/audit-templates',
        icon: Assignment
      }
    ]
  },
  {
    subheader: 'QAF',
    items: [
      {
        title: 'Syllabuses',
        href: '/syllabuses',
        icon: SyllabusIcon
      },
      {
        title: 'Observations',
        href: '/qaf/observations',
        icon: PageviewOutlined,
        notifications: LessonObservationNotification
      },
      {
        title: 'Certificate QA',
        href: '/qaf/certQA',
        icon: PolicyOutlined,
        notifications: CertificateQANotification
      },
      {
        title: 'Classroom QA',
        href: '/qaf/classroomQA',
        icon: PolicyOutlined,
        notifications: ClassroomQANotification
      },
      {
        title: 'Course Logs',
        href: '/qaf/course-logs',
        icon: History
      }
    ]
  },
  {
    subheader: 'Admin Settings',
    items: [
      {
        title: 'Admins',
        href: '/admins',
        icon: AssignmentInd
      },
      {
        title: 'Sent Emails',
        href: '/sent-emails',
        icon: Email
      }
    ]
  }
];

export const cryptairConfig = [
  {
    subheader: 'Management',
    items: [
      {
        title: 'Dashboard',
        href: '/dashboard',
        icon: DashboardIcon
      },
      {
        title: 'Companies',
        href: '/companies',
        icon: BarChartIcon
      }
      // {
      //   title: 'Individuals',
      //   href: '/individuals',
      //   icon: AssignmentInd
      // },
      // {
      //   title: 'Delegates',
      //   href: '/delegates',
      //   icon: AssignmentInd
      // }
      // {
      //   title: 'Managers',
      //   href: '/managers',
      //   icon: AssignmentInd
      // },
      // {
      //   title: 'Company Requests',
      //   href: '/approve-companies',
      //   icon: BarChartIcon
      // },
      // {
      //   title: 'Certificate Expiries',
      //   href: '/expiries',
      //   icon: BarChartIcon
      // },
      // {
      //   title: 'Background Checks',
      //   href: '/background-checks',
      //   icon: HowToReg
      // }
      // {
      //   title: 'XRAY Simulator',
      //   href: '/simulator',
      //   icon: Flip
      // }
    ]
  },
  {
    subheader: 'Courses',
    items: [
      // {
      //   title: 'Courses',
      //   href: '/courses',
      //   icon: LibraryBooks,
      //   items: [
      //     {
      //       title: 'Courses',
      //       href: '/courses'
      //     },
      //     {
      //       title: 'Modules',
      //       href: '/modules'
      //     },
      //     {
      //       title: 'Tests',
      //       href: '/tests'
      //     },
      //     {
      //       title: 'Questions',
      //       href: '/questions'
      //     },
      //     {
      //       title: 'Lessons',
      //       href: '/lessons'
      //     }
      //   ]
      // },
      // {
      //   title: 'Certificate Types',
      //   href: '/certificateTypes',
      //   icon: CardMembershipIcon
      // },
      // {
      //   title: 'CAA Numbers',
      //   href: '/caaNumbers',
      //   icon: CardMembershipIcon
      // },
      // {
      //   title: 'Categories',
      //   href: '/categories',
      //   icon: CardMembershipIcon
      // },
      // {
      //   title: 'Secondary Categories',
      //   href: '/secondarycategories',
      //   icon: CardMembershipIcon
      // },
      {
        title: 'Tutors',
        href: '/tutors',
        icon: AssignmentInd
      }
      // {
      //   title: 'Popular Courses',
      //   href: '/popular-courses',
      //   icon: Layers
      // },
      // {
      //   title: 'Tags',
      //   href: '/tags',
      //   icon: StyleIcon
      // },
      // {
      //   title: 'Audit Templates',
      //   href: '/audit-templates',
      //   icon: Assignment
      // }
    ]
  }
  // {
  //   subheader: 'Articles',
  //   items: [
  //     {
  //       title: 'News',
  //       href: '/news',
  //       icon: Article
  //     }
  //   ]
  // },
  // {
  //   subheader: 'Admin Settings',
  //   items: [
  //     {
  //       title: 'Admins',
  //       href: '/admins',
  //       icon: AssignmentInd
  //     }
  //   ]
  // }
];
